import { FC, useMemo, useRef } from 'react';
import { Spinner } from '@ibsys/kit';
import { useIsBetaFeatureEnabled } from '@/entities/beta-feature';
import { useConversationsAttributes } from '@/entities/conversations';
import { useDashboardPresets } from '@/entities/settings';
import { browserTimezone } from '@/shared/lib';
import { PageContainer } from '@/shared/ui';
import { useDashboardsPdf } from '../model/useDashboardsPdf';
import { useIndicatorTransitions } from '../model/useIndicatorTransitions';
import { useDashboardPresetEditor } from '../model/useDashboardPresetEditor';
import { useDashboardPresetSection } from '../model/useDashboardPresetSection';
import { useDashboardScreenState } from '../model/useDashboardScreenStore';
import { useDashboardScrollRestore } from '../model/useDashboardScrollRestore';
import { useDashboardSettings } from '../model/useDashboardSettings';
import { useDashboardData } from '../model/useDashboardData';
import { QualityFilters } from './QualityFilters';
import { DashboardPageHeader } from './DashboardPageHeader';
import { DashboardEditSection } from './DashboardPresetEditor/DashboardEditSection';
import { DashboardPresetGrid } from './DashboardPresetGrid/DashboardPresetGrid';
import { DashboardSummarySection } from './DashboardSummarySection';
import { DashboardDetailsSection } from './DashboardDetailsSection';

/** Имя скачиваемого файла с дашбордами. */
const PDF_FILE_NAME = 'quality-dashboards.pdf';

/**
 * Страница «Дашборды» — агрегированный отчёт по качеству
 * экспертных заключений: KPI, графики, heatmap, drill-down
 * по конкретным диалогам, мини-отчёт по консультанту.
 *
 * Все фильтры (консультант, период, режим heatmap, атрибутные фильтры)
 * живут на уровне страницы и прокидываются в секции. Все настройки фильтрации —
 * базовые фильтры панели, атрибутные фильтры, состав колонок, а также сортировка и
 * колоночные фильтры таблицы «Все детали» — синхронизируются с
 * `uiSettings.dashboardQuality` и восстанавливаются при следующем открытии дашборда.
 * Состояние drill-down остаётся транзиентным.
 *
 * Оркестрация разнесена по хукам: {@link useDashboardSettings} — персист-состояние и
 * его обработчики, {@link useDashboardData} — запросы и производные величины; карточки
 * рендерит {@link QualityDashboardContent}.
 */
export const QualityDashboardPage: FC = () => {
  // Переходы к диалогам — один механизм на все показатели: карточки сообщают выбранный элемент,
  // выборку из него собирает страница.
  const { selectHandlers, openConversation } = useIndicatorTransitions();

  // Пользовательские дашборды — бета-функция: при выключенной страница показывает стандартный
  // дашборд, как до её появления, а пресеты пользователя остаются нетронутыми в его настройках.
  const isPresetsEnabled = useIsBetaFeatureEnabled('CUSTOM_DASHBOARDS');

  // Пользовательский дашборд: применённый пресет показывается вместо стандартного дашборда,
  // а закреплённый ещё и открывается своим адресом.
  const { activePreset: appliedPreset } = useDashboardPresets();
  const { onActivePresetChange, isSectionPending } = useDashboardPresetSection();
  const activePreset = isPresetsEnabled ? appliedPreset : null;

  // Правка состава дашборда идёт на самой странице: пока режим включён, вместо дашборда показана
  // сетка правящегося.
  const presetEditor = useDashboardPresetEditor();
  const isEditing = isPresetsEnabled && presetEditor.draft !== null;

  // Правку пользователь вёл на самом дашборде — сохранённый и показывается вместо стандартного.
  const handleSaveDraft = () => {
    const presetId = presetEditor.save();
    if (presetId) onActivePresetChange(presetId);
  };

  // Граница области выгрузки: в файл идут карточки дашбордов, панель фильтров — нет.
  const dashboardsRef = useRef<HTMLDivElement>(null);
  const { downloadPdf, isBuilding: isPdfBuilding } = useDashboardsPdf(dashboardsRef, PDF_FILE_NAME);

  // Восстановление позиции прокрутки при возврате из диалога (после отрисовки сводки).
  const contentRef = useRef<HTMLDivElement>(null);

  // Состояние разбора (выбранная ячейка, консультант мини-отчёта, открытый звонок,
  // диапазон баллов) — в транзиентном сторе, чтобы пережить уход в карточку диалога
  // и возврат в рамках сессии (в отличие от фильтров панели, которые персистятся).
  const {
    drill,
    drillScoreFrom,
    drillScoreTo,
    selectedCallId,
    reportConsultant,
    selectCell,
    clearDrill,
    setDrillScoreFrom,
    setDrillScoreTo,
    resetDrillScore,
    setSelectedCallId,
    setReportConsultant,
  } = useDashboardScreenState();

  // Системная конфигурация атрибутов разговора — те же, что в гриде диалогов
  const { data: conversationAttributes } = useConversationsAttributes({ limit: 1000 });
  const availableAttributes = useMemo(
    () => conversationAttributes?.attributes ?? [],
    [conversationAttributes?.attributes],
  );

  // Персист-состояние дашборда (фильтры, настройки отображения) и его обработчики.
  const {
    filters,
    detailsSort,
    detailsFilters,
    display,
    activeAttributeFilters,
    allDetailsColumns,
    handleChangeFilters,
    handleChangeAttributeFilters,
    handleChangeDetailsSort,
    handleChangeDetailsFilters,
    handleChangeDisplay,
    handleResetFilters,
    handleChangeAllDetailsColumns,
  } = useDashboardSettings();

  // Запросы данных дашборда и производные величины.
  const {
    summaryData,
    summaryLoading,
    summaryFetching,
    heatmapData,
    heatmapLoading,
    reportData,
    detailsItems,
    detailsLoading,
    availableColumns,
    kpis,
    blockNames,
    consultantNames,
    visibleCriteriaAverages,
    distributionMax,
    trendMax,
    drillItems,
    drillLoading,
  } = useDashboardData({
    filters,
    activeAttributeFilters,
    detailsSort,
    detailsFilters,
    hiddenCriteria: display.hiddenCriteria,
    drill,
    reportConsultant,
    allDetailsColumns,
  });

  // Открытый звонок берём из списка ячейки: он всегда выбирается из него, и в общий
  // (обрезанный) список деталей мог не попасть.
  const selectedCall = useMemo(
    () => drillItems.find((r) => r.id === selectedCallId) ?? null,
    [drillItems, selectedCallId],
  );

  // Фильтры панели, общие для диаграмм пользовательского дашборда — и применённого, и правящегося.
  // Вместе с ними уходит часовой пояс браузера: даты текста результата собирает бэкенд, и без
  // пояса он привёл бы их к UTC.
  const presetFilters = useMemo(
    () => ({
      consultantName: filters.consultant || null,
      dateFrom: filters.dateFrom || null,
      dateTo: filters.dateTo || null,
      attributeFilters: activeAttributeFilters,
      timezone: browserTimezone(),
    }),
    [filters.consultant, filters.dateFrom, filters.dateTo, activeAttributeFilters],
  );

  // Сохранение/восстановление позиции прокрутки — готово, когда сводка отрисована.
  useDashboardScrollRestore(contentRef, !summaryLoading);

  // Пользовательский дашборд стандартной сводки не показывает и ждать её не должен: она нужна
  // ему только списком консультантов в панели фильтров, а он подтянется по готовности.
  // Раздел закреплённого дашборда, открытый адресом, ждёт своей готовности — иначе на нём
  // мелькнул бы стандартный дашборд.
  if (isSectionPending || (summaryLoading && !activePreset && !isEditing)) {
    return (
      <PageContainer limitedWidth={false}>
        <div className="flex justify-center items-center h-64">
          <Spinner className="size-8" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer limitedWidth={true}>
      <div ref={contentRef} className="h-full w-full pb-4 flex flex-col gap-4">
        <DashboardPageHeader
          presetName={activePreset?.name ?? null}
          isEditing={isEditing}
          draftName={presetEditor.draft?.name ?? ''}
          onChangeDraftName={presetEditor.setName}
          isDirty={presetEditor.isDirty}
          canSave={presetEditor.canSave}
          onSaveDraft={handleSaveDraft}
          onCancelEditing={presetEditor.cancel}
          isPresetsEnabled={isPresetsEnabled}
          isFetching={summaryFetching && !summaryLoading}
          isPdfBuilding={isPdfBuilding}
          onDownloadPdf={downloadPdf}
          onActivePresetChange={onActivePresetChange}
          onCreatePreset={presetEditor.startCreate}
          onEditPreset={presetEditor.startEdit}
        />

        <QualityFilters
          consultantNames={consultantNames}
          filters={filters}
          onChangeFilters={handleChangeFilters}
          hasDrill={drill !== null}
          availableAttributes={availableAttributes}
          activeAttributeFilters={activeAttributeFilters}
          onChangeAttributeFilters={handleChangeAttributeFilters}
          onReset={handleResetFilters}
          onClearDrill={clearDrill}
          zoneRanges={display.zoneRanges}
          hiddenCriteria={display.hiddenCriteria}
          hiddenBlocks={display.hiddenBlocks}
          onChangeDisplay={handleChangeDisplay}
        />

        {/* Граница области выгрузки в PDF: в файл идут карточки дашбордов, панель фильтров — нет. */}
        <div ref={dashboardsRef} className="flex flex-col gap-4">
          {/* Дашборд показан одним из трёх: правящийся, применённый пресет, стандартный. */}
          {isEditing && (
            <DashboardEditSection editor={presetEditor} filters={presetFilters} zoneRanges={display.zoneRanges} />
          )}

          {!isEditing && activePreset && (
            <DashboardPresetGrid
              preset={activePreset}
              filters={presetFilters}
              zoneRanges={display.zoneRanges}
              onSelect={selectHandlers.customChart}
              onOpenConversation={openConversation}
            />
          )}

          {!isEditing && !activePreset && (
            <>
              <DashboardSummarySection
                hiddenBlocks={display.hiddenBlocks}
                summaryData={summaryData}
                kpis={kpis}
                visibleCriteriaAverages={visibleCriteriaAverages}
                distributionMax={distributionMax}
                trendMax={trendMax}
                selectHandlers={selectHandlers}
                onOpenConversation={openConversation}
                consultantNames={consultantNames}
                reportConsultant={reportConsultant}
                reportData={reportData}
                onChangeReportConsultant={setReportConsultant}
                hiddenCriteria={display.hiddenCriteria}
                zoneRanges={display.zoneRanges}
              />

              <DashboardDetailsSection
                hiddenBlocks={display.hiddenBlocks}
                heatmapData={heatmapData}
                heatmapLoading={heatmapLoading}
                heatmapMode={filters.heatmapMode}
                drill={drill}
                onSelectCell={selectCell}
                drillItems={drillItems}
                blockNames={blockNames}
                drillLoading={drillLoading}
                selectedCallId={selectedCallId}
                drillScoreFrom={drillScoreFrom}
                drillScoreTo={drillScoreTo}
                onChangeScoreFrom={setDrillScoreFrom}
                onChangeScoreTo={setDrillScoreTo}
                onResetScore={resetDrillScore}
                onSelectCall={setSelectedCallId}
                selectedCall={selectedCall}
                detailsItems={detailsItems}
                detailsLoading={detailsLoading}
                availableColumns={availableColumns}
                availableAttributes={availableAttributes}
                allDetailsColumns={allDetailsColumns}
                onChangeAllDetailsColumns={handleChangeAllDetailsColumns}
                detailsSort={detailsSort}
                onSortChange={handleChangeDetailsSort}
                detailsFilters={detailsFilters}
                onFiltersChange={handleChangeDetailsFilters}
                onOpenConversation={openConversation}
              />
            </>
          )}
        </div>
      </div>
    </PageContainer>
  );
};
